/**
 * UserStampHistoryPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  Statistic,
  ModalLayout,
  UserList,
  Text,
} from 'components';
import {
  Avatar,
  Button,
  DatePicker,
  Radio,
  Table,
  Spin,
  Tag,
  Flex,
  InputNumber,
  Input,
} from 'antd';
import { Link } from 'react-router-dom';
import { encrypt } from 'utils/Crypto';
import { strCut, stringToMoneyFormat } from 'utils';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { downloadXlsx } from 'utils/XlsxDownloader';

const SUMMARY_VALUE = {
  all_users_point: 0,
  total_point: 0,
  not_expired_point: 0,
  used_point: 0,
  expired_point: 0,
  expired_7ago_point: 0,
};

const TYPE_VAlUE = {
  EARNED: {
    color: 'blue',
    label: '적립',
  },
  EXPIRED: {
    color: 'red',
    label: '만료',
  },
  USED: {
    color: 'orange',
    label: '사용',
  },
};
const EXPIRY = 7;
const EXPIRY_DATE = dayjs().startOf('day').unix();
/**----------------------------------------
 * [Component] 총관리자: 회원관리 -> 스탬프내역
 * ----------------------------------------
 */
const UserStampHistoryPresenter = ({
  userList,
  permission,
  loading,
  currentUser,
  setCurrentUser,
  setPeriod,
  StampHistoryList,
  onChange,
  inputValue,
  setInputValue,
  onStampUpdate,
  setLoading,
  INITIAL_VALUE,
  StampSetting,
  onSelect,
  selectedList,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [filterList, setFilterList] = React.useState(userList);
  const [summary, setSummary] = React.useState(SUMMARY_VALUE);
  const [currentUserData, setCurrentUserData] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [buttonType, setButtonType] = React.useState(null);
  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  const formItem = [
    {
      label: '회원 유형',
      form: [
        {
          input: (
            <Radio.Group
              value={inputValue.type}
              onChange={(e) => onChange('type', e.target.value)}
            >
              <Radio value={''}>전체</Radio>
              <Radio value={'MEMBER'}>회원</Radio>
              <Radio value={'NONE'}>비회원</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '적립 유무',
      form: [
        {
          input: (
            <Radio.Group
              value={inputValue.isReward}
              onChange={(e) => onChange('isReward', e.target.value)}
            >
              <Radio value={''}>전체</Radio>
              <Radio value={'REWARD'}>적립</Radio>
              <Radio value={'NOT_REWARD'}>비적립</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '적립일',
      form: [
        {
          input: (
            <Flex gap="large" size="large">
              <Radio.Group
                size="large"
                value={inputValue.value}
                onChange={(e) => {
                  setInputValue((prev) => ({
                    ...prev,
                    value: e.target.value,
                    start_date: dayjs()
                      .subtract(e.target.value, 'day')
                      .startOf('day'),
                    end_date: dayjs().endOf('day'),
                  }));
                }}
              >
                <Radio.Button value={1}>1일</Radio.Button>
                <Radio.Button value={7}>7일</Radio.Button>
                <Radio.Button value={30}>30일</Radio.Button>
                <Radio.Button value={90}>90일</Radio.Button>
              </Radio.Group>
              <Flex align="center" gap={4}>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dayjs(inputValue.start_date)}
                  onChange={(e) =>
                    setInputValue((prev) => ({
                      ...prev,
                      start_date: dayjs(e).startOf('day'),
                    }))
                  }
                  maxDate={inputValue.end_date}
                />
                <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dayjs(inputValue.end_date)}
                  onChange={(e) =>
                    setInputValue((prev) => ({
                      ...prev,
                      end_date: dayjs(e).endOf('day'),
                    }))
                  }
                  minDate={inputValue.start_date}
                />
              </Flex>
            </Flex>
          ),
        },
      ],
    },
  ];

  const col = [
    {
      title: '스탬프번호',
      dataIndex: 'stamp_history_id',
      key: 'stamp_history_id',
      width: 100,
      fixed: 'left',
      sorter: {
        compare: (a, b) => a.stamp_history_id - b.stamp_history_id,
        multiple: 2,
      },
      render: (stamp_history_id) => <>{stamp_history_id}</>,
    },
    {
      title: '이름(아이디)',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 150,
      render: () => (
        <>
          <Avatar style={{ marginRight: 5 }}>
            {currentUserData.user_name}
          </Avatar>
          <Link
            to={`/users/list?modal=${encodeURIComponent(
              encrypt(currentUserData.user_id)
            )}`}
          >
            {strCut(currentUserData.user_name, 10)}(
            {strCut(currentUserData.user_account, 10)})
          </Link>
        </>
      ),
    },
    {
      title: '휴대폰번호',
      dataIndex: 'user_phone',
      key: 'user_phone',
      width: 150,
      render: () => <>{currentUserData.user_phone?.replace(/\(\d+\)/, '')}</>,
    },
    {
      title: '매장명',
      dataIndex: 'shop_information_name',
      key: 'shop_information_name',
      width: 150,
      render: (shop_information_name) => <>{shop_information_name}</>,
    },
    {
      title: '적립경로',
      dataIndex: 'reward_type',
      key: 'reward_type',
      width: 150,
      render: (reward_type) => <>{reward_type}</>,
    },
    {
      title: '적립 타입',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      filters: [
        { text: '적립', value: 'EARNED' },
        { text: '만료', value: 'EXPIRED' },
        { text: '사용', value: 'USED' },
      ],
      onFilter: (value, record) => {
        let stampType;
        if (!record?.use_date && dayjs().unix() < record?.expired_date) {
          stampType = 'EARNED';
        } else if (record?.use_date) {
          stampType = 'USED';
        } else {
          stampType = 'EXPIRED';
        }
        return stampType.includes(value);
      },
      render: (_, _item) => {
        let type;
        if (!_item?.use_date && dayjs().unix() < _item?.expired_date) {
          type = 'EARNED';
        } else if (_item?.use_date) {
          type = 'USED';
        } else {
          type = 'EXPIRED';
        }
        return (
          <>
            <Tag {...{ color: TYPE_VAlUE[type]?.color }}>
              {TYPE_VAlUE[type]?.label}
            </Tag>
          </>
        );
      },
    },
    {
      title: '적립수량',
      dataIndex: 'stamp_num',
      key: 'stamp_num',
      width: 150,
      sorter: {
        compare: (a, b) => a.stamp_num - b.stamp_num,
        multiple: 2,
      },
      render: (stamp_num) => <>{stamp_num}</>,
    },
    {
      title: '사용일',
      dataIndex: 'use_date',
      key: 'use_date',
      width: 155,
      sorter: {
        compare: (a, b) => a.use_date - b.use_date,
        multiple: 2,
      },
      render: (use_date) =>
        use_date ? dayjs(use_date * 1000).format('YYYY-MM-DD(HH:mm)') : '-',
    },

    {
      title: '만료 예정일',
      dataIndex: 'expired_date',
      key: 'expired_date',
      width: 155,
      sorter: {
        compare: (a, b) => a.expired_date - b.expired_date,
        multiple: 2,
      },
      render: (expired_date) =>
        dayjs(expired_date * 1000).format('YYYY-MM-DD(HH:mm)'),
    },
    {
      title: '적립일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 155,
      sorter: {
        compare: (a, b) => a.created_at - b.created_at,
        multiple: 2,
      },
      render: (created_at) =>
        dayjs(created_at * 1000).format('YYYY-MM-DD(HH:mm)'),
    },
  ];

  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  /**
   * 포인트 요약 계산
   * --
   * @param {*} type
   * @returns
   */
  const calculateStamp = (type) => {
    const nowDateUnix = dayjs().unix();
    const user = userList?.find((user) => user?.user_id === currentUser);
    switch (type) {
      case 'ALL_USERS_STAMP':
        return filterList?.reduce((acc, user) => {
          const userStamp = user?.stamp_history?.reduce((acc, stamp) => {
            return (acc += stamp?.stamp_num);
          }, 0);
          return acc + userStamp;
        }, 0);

      case 'ALL_STAMP':
        return user?.stamp_history?.reduce((acc, stamp) => {
          return (acc += stamp?.stamp_num);
        }, 0);

      case 'NOT_EXPIRED_STAMP':
        return user?.stamp_history?.reduce((acc, stamp) => {
          if (!stamp?.use_date && nowDateUnix < stamp?.expired_date) {
            acc += stamp?.stamp_num; // 유효한 포인트 합산
          }
          return acc;
        }, 0);

      case '7AGO_EXPIRED_STAMP':
        return user?.stamp_history?.reduce((acc, stamp) => {
          const expDay = dayjs
            .unix(stamp?.expired_date)
            .diff(dayjs.unix(EXPIRY_DATE), 'day');
          if (
            nowDateUnix < stamp?.expired_date &&
            expDay >= 0 &&
            expDay <= EXPIRY
          ) {
            acc += stamp?.stamp_num; // 유효한 포인트 합산
          }
          return acc;
        }, 0);

      case 'USED_STAMP':
        return user?.stamp_history?.reduce((acc, stamp) => {
          if (stamp?.use_date) {
            return (acc += stamp?.stamp_num);
          }
          return acc;
        }, 0);

      case 'EXPIRED_STAMP':
        return user?.stamp_history?.reduce((acc, stamp) => {
          const isExpired =
            !stamp?.use_date && nowDateUnix > stamp?.expired_date;
          return (acc += isExpired ? stamp?.stamp_num : 0);
        }, 0);

      default:
        return 0;
    }
  };
  /* ===== FUNCTION ===== */
  /**
   * 포인트 조건 검색
   * --
   * @param {string} t
   */
  const handleSearch = (t) => {
    setLoading(true);
    const searchType = t.toLocaleUpperCase();
    let arr = userList;
    switch (searchType) {
      case 'SEARCH':
        const { type, isReward } = inputValue;
        if (type) {
          if (type === '') {
            arr = arr.map((user) => user);
          }
          //
          if (type === 'MEMBER') {
            arr = arr.filter((user) =>
              user.user_type === 'MEMBER' ? true : false
            );
          }
          //
          if (type === 'NONE') {
            arr = arr.filter((user) =>
              user.user_type === 'NONE' ? true : false
            );
          }
        }
        if (isReward) {
          if (isReward === '') {
            arr = arr;
          }
          //
          if (isReward === 'REWARD') {
            arr = arr?.filter((user) => user?.stamp_history?.length > 0);
          }
          //
          if (isReward === 'NOT_REWARD') {
            arr = arr?.filter((user) => user?.stamp_history?.length === 0);
          }
        }
        break;
      default:
        break;
    }
    const sort = arr.map((user) => ({
      ...user,
      stamp_history: user?.stamp_history.sort(
        (a, b) => b?.created_at - a?.created_at
      ),
    }));
    setSummary((prev) => ({
      ...prev,
      all_users_stamp: sort?.reduce((acc, user) => {
        const userStamp = user?.stamp_history?.reduce((acc, stamp) => {
          return (acc += stamp?.stamp_num);
        }, 0);
        return acc + userStamp;
      }, 0),
    }));
    setFilterList(sort);
    setCurrentUser(null);
    setCurrentUserData(null);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  /* ===== Hooks ===== */
  /**
   * 회원 선택
   * ---
   */
  React.useEffect(() => {
    const filterUser = filterList?.find(
      (user) => user?.user_id === currentUser
    );
    setCurrentUserData(filterUser);
  }, [currentUser, filterList]);
  /**
   * 요약
   * ---
   */
  React.useEffect(() => {
    setSummary((prev) => ({
      ...prev,
      total_stamp: stringToMoneyFormat(calculateStamp('ALL_STAMP')),
      not_expired_stamp: stringToMoneyFormat(
        calculateStamp('NOT_EXPIRED_STAMP')
      ),
      used_stamp: stringToMoneyFormat(calculateStamp('USED_STAMP')),
      expired_stamp: stringToMoneyFormat(calculateStamp('EXPIRED_STAMP')),
      expired_7ago_stamp: stringToMoneyFormat(
        calculateStamp('7AGO_EXPIRED_STAMP')
      ),
    }));
    onSelect('reset');
  }, [currentUser, StampHistoryList]);
  /**
   * 검색
   * --
   */
  React.useEffect(() => {
    handleSearch('search');
  }, [StampHistoryList]);

  return (
    <>
      {/* === Loading - start === */}
      <Spin spinning={loading} indicator={<LoadingOutlined spin />}>
        {/* ===== 헤더&필터 - start ===== */}
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          {/* === 타이틀 === */}
          <Title style={{ ...styles.title }}>스탬프 내역</Title>

          {/* === 필터 - start === */}
          <ExpandableContainer borderRadius={4} padding={20}>
            {formItem.map((_item, _index) => (
              <div
                key={`form_${_index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 0px',
                }}
              >
                <div style={{ width: 120, fontSize: 13, fontWeight: 400 }}>
                  <label>{_item.label}</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {_item.form.map((_subitem, _subkey) => (
                    <React.Fragment key={`input_${_subkey}`}>
                      {_subitem.input}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </ExpandableContainer>

          {/* 검색/초기화 버튼 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              marginTop: 13,
            }}
          >
            <Button
              size="large"
              type="primary"
              style={{
                ...styles.button,
                background: 'var(--primary-color)',
              }}
              onClick={() => {
                setPeriod({
                  start_date: inputValue.start_date.unix(),
                  end_date: inputValue.end_date.unix(),
                });
                handleSearch('search');
              }}
            >
              검색
            </Button>
            <Button
              size="large"
              style={{ ...styles.button }}
              onClick={() => {
                setPeriod({
                  start_date: INITIAL_VALUE.start_date.unix(),
                  end_date: INITIAL_VALUE.end_date.unix(),
                });
                setInputValue(INITIAL_VALUE);
              }}
            >
              초기화
            </Button>
          </div>
          {/* 검색/초기화 버튼 끝 */}
          {/* === 필터 - end === */}
        </Content>
        {/* ===== 헤더&필터 - end ===== */}

        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          {/* === 통계 - start === */}
          <Content
            maxWidth={'100%'}
            padding={0}
            backgroundColor={'none'}
            style={{
              marginBottom: 4,
              display: 'flex',
              gap: '2px',
            }}
          >
            {[
              {
                title: '기간별 회원 총 적립 스탬프',
                value: summary.all_users_stamp,
                suffix: '개',
              },
              {
                title: '회원 총 적립 스탬프',
                value: summary.total_stamp,
                suffix: '개',
              },
              {
                title: '미사용 스탬프',
                value: summary.not_expired_stamp,
                suffix: '개',
              },
              {
                title: '사용 스탬프',
                value: summary.used_stamp,
                suffix: '개',
              },
              {
                title: '만료 예정 스탬프(7일 기준)',
                value: summary.expired_7ago_stamp,
                suffix: '개',
              },
              {
                title: '만료 스탬프',
                value: summary.expired_stamp,
                suffix: '개',
              },
            ].map((_item, _index) => (
              <div
                key={`${_item.label}-${_index}`}
                className="boxShadow"
                style={{
                  background: '#FFFFFF',
                  borderRadius: 4,
                  width: '100%',
                  margin: '2px',
                  padding: '26px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Statistic
                  style={{ justifyContent: 'center' }}
                  title={_item.title}
                  value={_item.value}
                  suffix={_item.suffix}
                  direction={'row'}
                />
              </div>
            ))}
          </Content>
          {/* === 통계 - end === */}

          {/* ===== 하단컨텐츠 - start ===== */}
          <Row gutter={[1, 1]} className="boxShadow">
            {/* === 회원목록 - start === */}
            {permission === 'ADMIN' && (
              <Col x={4}>
                <UserList
                  users={filterList}
                  selectedUser={currentUser}
                  fetch={false}
                  onChange={setCurrentUser}
                  // query={{ filter: 'user_name' }}
                  maxHeight={611}
                />
              </Col>
            )}
            {/* === 회원목록 - end === */}

            {/* === 스탬프목록 - start === */}
            <Col x={permission === 'ADMIN' ? 20 : 24}>
              <Content
                maxWidth={'100%'}
                width={1400}
                height={'100%'}
                minHeight={490}
                maxHeight={662}
                borderRadius={4}
                padding={20}
              >
                {/* === 헤더 === */}
                <div>
                  <Title
                    size={4}
                    style={{ marginBottom: 10 }}
                    subButton={
                      <Flex gap={4}>
                        <Button
                          onClick={() => {
                            downloadXlsx({
                              columns: col,
                              rows: currentUserData?.stamp_history?.map(
                                (_item) => {
                                  let type;
                                  if (
                                    !_item?.use_date &&
                                    dayjs().unix() < _item?.expired_date
                                  ) {
                                    type = 'EARNED';
                                  } else if (_item?.use_date) {
                                    type = 'USED';
                                  } else {
                                    type = 'EXPIRED';
                                  }
                                  return {
                                    ..._item,
                                    user_name: `${currentUserData?.user_name}(${currentUserData?.user_account})`,
                                    user_phone: currentUserData?.user_phone,
                                    type: TYPE_VAlUE[type]?.label,
                                  };
                                }
                              ),
                              fileName: '스탬프_내역',
                            });
                          }}
                        >
                          엑셀로 다운로드
                        </Button>
                        {/* ====만료 지급 버튼 주석 풀면 사용가능 */}
                        <Button
                          disabled={!currentUser}
                          onClick={() => {
                            setIsOpen(!isOpen);
                            setButtonType('REWARD');
                          }}
                        >
                          스탬프 지급
                        </Button>

                        <Button
                          disabled={!currentUser}
                          onClick={() => {
                            setIsOpen(!isOpen);
                            setButtonType('EXPRIED');
                          }}
                        >
                          {selectedList?.length}건 스탬프 차감
                        </Button>
                        {/* ====만료 지급 버튼 주석 풀면 사용가능 */}
                      </Flex>
                    }
                  >
                    <span>
                      검색결과 총{' '}
                      <a>{currentUserData?.stamp_history?.length || 0}</a>건
                    </span>
                  </Title>
                </div>

                <Table
                  size="small"
                  pagination={{
                    pageSize: 50,
                  }}
                  //지급 만료 처리 버튼 해제시 주석 해제
                  rowSelection={{
                    columnWidth: 50, // 체크박스 열 너비 조정
                    size: 'large',
                    type: 'checkbox',
                    onSelect: (record, selected, selectedRows, nativeEvent) => {
                      onSelect(selected ? 'checked' : 'unchecked', record);
                    },
                    onSelectAll: (selected, selectedRows, changeRows) => {
                      onSelect(
                        selected ? 'all-checked' : 'all-unchecked',
                        selectedRows
                      );
                    },
                    selectedRowKeys: selectedList?.map((_item) => _item?.key),
                  }}
                  scroll={{
                    x: 1250,
                    y: 49 * 10,
                  }}
                  dataSource={currentUserData?.stamp_history}
                  columns={col}
                />
                {/* === 스탬프목록 목록 === */}
              </Content>
            </Col>
          </Row>
          {/* ===== 하단컨텐츠 - end ===== */}
        </Content>
      </Spin>
      {/* === Loading - start === */}

      <ModalLayout
        open={isOpen}
        onCancel={() => {
          setInputValue((prev) => ({
            ...prev,
            reward_stamp: 0,
            expired_date: 0,
          }));
          setIsOpen(!isOpen);
        }}
        width={400}
        centered
      >
        <Content maxWidth={'100%'} padding={0}>
          {buttonType === 'REWARD' ? (
            <>
              <Flex vertical={true} gap={5} style={{ marginBottom: '10px' }}>
                <p>스탬프를 지급합니다.</p>
                <p>
                  설정된 쿠폰 발급 스탬프 조건 : {StampSetting?.goal_num} 개
                </p>
                <p>
                  설정된 스탬프 만료 일 : 지급일 기준{' '}
                  {StampSetting?.expired_date}일 뒤 만료
                </p>

                <Flex
                  alignItems="center"
                  gap={5}
                  style={{ marginBottom: '5px' }}
                >
                  <Text style={{ display: 'flex', alignItems: 'center' }}>
                    {'지급일 기준 '}
                  </Text>
                  <InputNumber
                    value={inputValue?.expired_date}
                    onChange={(e) => onChange('expired_date', e)}
                    placeholder="만료 일수를 입력해 주세요"
                    style={{ flex: 0.8 }}
                    suffix={'일'}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                  />
                  <Text style={{ display: 'flex', alignItems: 'center' }}>
                    {'뒤 만료'}
                  </Text>
                </Flex>

                <InputNumber
                  value={inputValue?.reward_stamp}
                  onChange={(e) => onChange('reward_stamp', e)}
                  placeholder={'지급 스탬프'}
                  suffix={'개'}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  style={{ width: '100%' }}
                />
              </Flex>
            </>
          ) : (
            <>
              <Flex vertical={true} gap={10} style={{ marginBottom: '10px' }}>
                <p>입력된 만료일을 기준으로 스탬프를 차감합니다.</p>
                <DatePicker
                  size="large"
                  showTime={true}
                  style={{ width: '100%' }}
                  value={
                    inputValue.update_date
                      ? dayjs(inputValue.update_date)
                      : null
                  }
                  onChange={(e) => {
                    onChange('update_date', e);
                  }}
                  placeholder="날짜 선택"
                  format="YYYY-MM-DD HH시 mm분"
                />
              </Flex>
            </>
          )}

          <Flex vertical="true">
            <Button
              type="primary"
              danger={buttonType === 'EXPRIED' && true}
              onClick={() => {
                onStampUpdate(currentUserData, inputValue, buttonType);
                setTimeout(() => {
                  setIsOpen(false);
                  setInputValue((prev) => ({
                    ...prev,
                    reward_stamp: 0,
                    expired_date: 0,
                    update_date: 0,
                  }));
                  onSelect('reset');
                  setButtonType(null);
                }, 1000);
              }}
            >
              {buttonType === 'REWARD' ? '지급' : '수정하기'}
            </Button>
          </Flex>
          {/* <Flex>
            <Text style={{ marginBottom: '10px' }}>
              {buttonType === 'REWARD'
                ? '스탬프를 지급합니다.'
                : '스탬프를 적립된 순서대로 차감합니다.'}
            </Text>
          </Flex>
          {buttonType === 'REWARD' ? (
            <Flex alignItems="center" gap={5} style={{ marginBottom: '5px' }}>
              <Text style={{ display: 'flex', alignItems: 'center' }}>
                {'적립일 기준 '}
              </Text>
              <InputNumber
                value={inputValue?.expired_date}
                onChange={(e) => onChange('expired_date', e)}
                placeholder="만료 일수를 입력해 주세요"
                style={{ flex: 0.8 }}
                suffix={'일'}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
              />
              <Text style={{ display: 'flex', alignItems: 'center' }}>
                {'만료'}
              </Text>
            </Flex>
          ) : (
            <Flex style={{ marginBottom: '5px' }}>
              <Text>
                {' '}
                차감 가능 포인트: {calculatePoint('NOT_EXPIRED_POINT')}P
              </Text>
            </Flex>
          )}
          <Flex gap={10} vertical="true">
            <InputNumber
              value={inputValue?.point}
              onChange={(e) => onChange('point', e)}
              placeholder={
                buttonType === 'REWARD' ? '지급 적립금' : '차감 적립금'
              }
              max={
                buttonType === 'DEDUCTED' && calculatePoint('NOT_EXPIRED_POINT')
              }
              suffix={'P'}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              style={{ width: '100%' }}
            />
            <Input
              value={inputValue?.memo}
              onChange={(e) => onChange('memo', e.target.value)}
              placeholder={'사유를 입력 하세요'}
            ></Input>
            <Button
              type="primary"
              danger={buttonType === 'REWARD' ? false : true}
              onClick={() => {
                onReservePoint(currentUserData, inputValue, buttonType);
                setTimeout(() => {
                  setIsOpen(false);
                  setInputValue({
                    point: 0,
                    expired_date: 0,
                    memo: null,
                  });
                  setButtonType(null);
                }, 1000);
              }}
            >
              {buttonType === 'REWARD' ? '지급' : '차감'}
            </Button>
          </Flex> */}
        </Content>
      </ModalLayout>
    </>
  );
};
UserStampHistoryPresenter.defaultProps = {
  users: [],
};
export default UserStampHistoryPresenter;
